import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ModalService } from 'src/app/services/modal/modal.service';
import { selectLanguage } from 'src/app/store/centerResponse/center.selector';
import { InvitationsInterface } from 'src/app/store/interface/invitations.interface';
import * as participantSelection from 'src/app/store/participant/participant.selector';
import { rootAction } from 'src/app/store/root.action';
import { selectDataParams } from 'src/app/store/root.selector';
import { ToastInterface } from '../../store/interface/toast.interface';
import { participantAction } from '../../store/participant/participant.action';
import { Constant } from '../../utils/constant';
import { InviteParticipantsService } from './invite-participants.service';

@Component({
  selector: 'app-invite-participants-modal',
  templateUrl: './invite-participants-modal.component.html',
  styleUrls: ['./invite-participants-modal.component.scss'],
})
export class InviteParticipantsModalComponent implements OnInit, OnDestroy {
  public form: FormArray;
  public invitations: InvitationsInterface;
  public emails: string[];
  private _unsubscriber$: Subject<void> = new Subject();
  public toast: ToastInterface;

  readonly isSettingParticipants$: Observable<boolean> = this._store.select(
    participantSelection.isSettingParticipants
  );

  constructor(
    private _formBuilder: FormBuilder,
    private _store: Store,
    private _invite: InviteParticipantsService,
    private _modal: ModalService
  ) {
    this.emails = [];
    this.invitations = { email: [], roomCode: '', lang: '' };
    this.toast = { text: '', type: '', closed: true };
  }

  ngOnInit(): void {
    this._buildForm();
  }

  private _buildForm(): void {
    this.form = this._formBuilder.array([]);
    this.form.push(
      this._formBuilder.group({
        email: [
          '',
          [Validators.required, Validators.pattern(Constant.EMAILS_PATTERN)],
        ],
        admin: [false],
      })
    );
  }

  public submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();

      this.toast = { text: 'failedInvitations', type: 'danger', closed: true };

      this._store.dispatch(rootAction.showToast({ toast: { ...this.toast } }));
      this._store.dispatch(participantAction.setParticipantsFail());
      return;
    }

    const emails = this.form.value.map((value: any) => value.email);

    const participants$ = this._store
      .select(participantSelection.selectParticipant)
      .pipe(take(1));
    const dataParams$ = this._store.select(selectDataParams).pipe(
      filter((res: any) => res.client !== ''),
      take(1)
    );
    const langs$ = this._store.select(selectLanguage).pipe(take(1));

    combineLatest([participants$, dataParams$, langs$])
      .pipe(take(1))
      .subscribe(([participants, dataParams, langs]) => {
        const emailsAux = Array.from(
          new Set([...(participants || []), ...emails])
        );
        this._saveEmails(emailsAux);

        const roomCode = dataParams.client.substring(
          dataParams.client.lastIndexOf('/') + 1
        );
        const lang = langs && langs.length > 0 ? langs[0].toLowerCase() : 'es';

        const invitations: InvitationsInterface = {
          email: emails,
          roomCode,
          lang,
        };

        this.toast = {
          text: 'successfulInvitations',
          type: 'success',
          closed: true,
        };

        this._invite.sendInvitations(invitations).subscribe(
          () => {
            this._modal.hide();
            this._store.dispatch(
              rootAction.showToast({ toast: { ...this.toast } })
            );
            this._store.dispatch(participantAction.setParticipantsSuccess());
          },
          (error) => {
            console.error(error);
            this._store.dispatch(participantAction.setParticipantsFail());
          }
        );
      });
  }

  private _saveEmails(participants: string[]): void {
    this._store.dispatch(
      participantAction.setParticipants({
        participants,
      })
    );
  }

  ngOnDestroy(): void {
    this._unsubscriber$.next();
    this._unsubscriber$.unsubscribe();
  }
}
